import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

/**
 * @typedef {object} ExternalIntegrationsPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} ExternalIntegrationsFilter
 * @property {string} search
 */

/**
 * @typedef {object} ExternalIntegrationsSorting
 * @property {string} orderBy
 * @property {string} order
 */

/**
 * @typedef {object} ExternalIntegrationData
 * @property {number} id
 * @property {string} integration_name
 * @property {string} integration_type
 * @property {string} url
 * @property {object} headers
 * @property {object} body
 * @property {object} configuration
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {object} SellerData
 * @property {number | null} id
 * @property {number | null} rut
 * @property {string | null} permission
 * @property {integer | null} admin
 * @property {object | null} company
 * @property {object | null} fares
 * @property {object | null} user
 * @property {Array<object>} warehouses
 * @property {Array<object>} operators
 */

/**
 * Initialize status from localStorage to maintain table
 * persistence when reloading page
 *
 * @returns {{filters: ExternalIntegrationsFilter, pagination: ExternalIntegrationsPagination, sorting: ExternalIntegrationsSorting}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("externalIntegrationsFilter");
  const defaults = {
    filters: { search: "" },
    pagination: { page: 1, rowsPerPage: 20 },
    sorting: { orderBy: "integration_name", order: "asc" },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      return {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
        sorting: { ...defaults.sorting, ...(savedState.sorting || {}) },
      };
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "externalIntegrationsFilter": ${error}`,
      );
      return defaults;
    }
  }
  return defaults;
};

/**
 * Custom Hook to manage DATA state for the Admin External Integrations page
 *
 * Handles data fetching (GET), templates fetching, pagination, sorting, filtering,
 * and CRUD operations (POST, PATCH, DELETE)
 * It does NOT manage UI state
 *
 * @returns {{
 *   integrations: Array<ExternalIntegrationData>,
 *   sellers: Array<SellerData>,
 *   loading: boolean,
 *   error: string | null,
 *   pagination: ExternalIntegrationsPagination,
 *   filters: ExternalIntegrationsFilter,
 *   sorting: ExternalIntegrationsSorting,
 *   integrationsCount: number,
 *   handleChangePage: (newPage: number) => void,
 *   handleChangeRowsPerPage: (newRows: number) => void,
 *   handleSortChange: (orderBy: string) => void,
 *   createIntegration: (integrationData: object) => Promise<void>,
 *   updateIntegration: (integrationId: number, integrationData: object) => Promise<void>,
 *   deleteIntegration: (integrationId: number) => Promise<void>,
 *   fetchSellersByAdmin: () => Promise<void>
 *   refetchIntegrations: () => Promise<void>
 * }}
 */

export function useAdminExternalIntegrations() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);

  const [integrations, setIntegrations] = useState([]);
  const [integrationsCount, setIntegrationsCount] = useState(0);

  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSortChange = useCallback((orderBy) => {
    setSorting((previous) => ({
      orderBy: orderBy,
      order:
        previous.orderBy === orderBy && previous.order === "asc"
          ? "desc"
          : "asc",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };

    try {
      const {
        data: { results: externalIntegrations, count },
      } = await axios.get("admins/get_external_integrations/", {
        params,
      });

      setIntegrations(externalIntegrations);
      setIntegrationsCount(count);
    } catch (error) {
      console.error(`Error obteniendo integraciones: ${error}`);
      setError("No se pudieron obtener las configuraciones de integración");
    } finally {
      setLoading(false);
    }
  }, [pagination, filters, sorting]);

  const createIntegration = useCallback(
    async (integrationData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("admins/create_external_integration/", {
          external_integration: integrationData,
        });
        fetchIntegrations();
      } catch (error) {
        console.error(`Error al crear integración: ${error}`);
        setError("Error al crear una nueva integración externa");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchIntegrations],
  );

  const updateIntegration = useCallback(
    async (integrationId, integrationData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.patch(
          `admins/update_external_integration/${integrationId}/`,
          {
            external_integration: integrationData,
          },
        );
        fetchIntegrations();
      } catch (error) {
        console.error(`Error al actualizar integración: ${error}`);
        setError("Error al actualizar la integración externa");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchIntegrations],
  );

  const deleteIntegration = useCallback(
    async (integrationId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(
          `admins/delete_external_integration/${integrationId}/`,
        );
        fetchIntegrations();
      } catch (error) {
        console.error(`Error al eliminar integración: ${error}`);
        setError("Error al intentar eliminar la integración externa");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchIntegrations],
  );

  const fetchSellersByAdmin = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { serialized_sellers },
      } = await axios.get("admins/sellers-list/");
      const sellers = serialized_sellers.map((seller) => ({
        id: seller.id,
        name: seller.company?.social_reason
      }));

      setSellers(sellers);
    } catch (error) {
      console.error(`No se pudo obtener los sellers: ${error}`);
      setError("No se pudo obtener los sellers");
    } finally {
      setLoading(false);
    }
  }, []);

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    localStorage.setItem(
      "externalIntegrationsFilter",
      JSON.stringify({
        filters,
        pagination,
        sorting,
      }),
    );
  }, [filters, pagination, sorting]);

  useEffect(() => {
    fetchIntegrations();
    fetchSellersByAdmin();
  }, [fetchIntegrations, fetchSellersByAdmin]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    integrations,
    sellers,
    loading,
    error,
    pagination,
    filters,
    sorting,
    integrationsCount,

    // MANNERS (Setters y Actions)
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    refetchIntegrations: fetchIntegrations,
    refetchSellersByAdmin: fetchSellersByAdmin,
  };
}
